// plugins/code-tokenizer.ts — Mini-lexer/tokenizer para Auto-Highlight
// Gera HTML com <span class="kw|str|cm|fn|nu|sp"> a partir do código fonte

type Lang = string;

const COMMON_KEYWORDS = [
  'if','else','for','while','return','function','const','let','var','class','new','switch','case','break','continue','import','export','try','catch','finally','throw','extends','implements','interface','public','protected','private','static','package','def','elif','yield','await','async','foreach','in','as','from','namespace'
];

const KEYWORDS: Record<string, string[]> = {
  php: ['function','public','protected','private','class','namespace','use','echo','print','die','return','foreach','as','if','else','elseif','switch','case','break','continue','new','const','var','global','static','isset','empty','array','count','list','unset','try','catch','finally','throw','yield','include','require','include_once','require_once'],
  javascript: ['function','return','const','let','var','if','else','for','while','switch','case','break','continue','class','new','import','export','try','catch','finally','await','async','=>'],
  typescript: ['type','interface','implements','extends','readonly','abstract','public','private','protected','enum','namespace'],
  python: ['def','class','return','if','elif','else','for','while','import','from','as','try','except','finally','with','lambda','yield','pass','break','continue'],
  java: ['public','private','protected','class','interface','extends','implements','new','return','if','else','for','while','switch','case','break','continue','import','package','static'],
  csharp: ['public','private','protected','class','interface','using','namespace','new','return','if','else','for','while','switch','case','break','continue','static'],
  go: ['func','package','import','return','if','else','for','range','switch','case','defer','go'],
  rust: ['fn','let','mut','pub','impl','struct','enum','use','mod','crate','match','if','else','loop','for','while','return'],
  ruby: ['def','class','module','end','if','else','elsif','unless','begin','rescue','ensure','return','yield'],
  sql: ['select','from','where','insert','into','update','delete','join','left','right','inner','outer','on','group','by','order','limit','as'],
  bash: ['if','then','else','fi','for','in','do','done','case','esac','function','return'],
};

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function getKeywordsFor(lang: Lang): Set<string> {
  const k = KEYWORDS[lang] && KEYWORDS[lang].length ? KEYWORDS[lang] : COMMON_KEYWORDS;
  return new Set(k.map(x => x.toLowerCase()));
}

export function tokenize(code: string, lang: Lang = ''): string {
 
  code = code.replace(/\r\n?/g, '\n');

  const keywords = getKeywordsFor(lang);

  const regex = /\/\*[\s\S]*?\*\/|<!--?[\s\S]*?-->|```[\s\S]*?```|`(?:[^`\\]|\\.)*`|"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|\/\/.*?$|#.*?$|--.*?$|0x[a-fA-F0-9]+|\b\d+\.\d+\b|\b\d+\b|[A-Za-z_\$@][A-Za-z0-9_\$@]*|\s+|./mg;

  let out = '';
  let m: RegExpExecArray | null;

  while ((m = regex.exec(code)) !== null) {
    const token = m[0];
    let cls: string | null = null;

    if (/^\/\*|^<!--|^\/\/|^#|^--/.test(token)) {
      cls = 'cm';
    } else if (/^```/.test(token)) {
      cls = 'str';
    } else if (/^[`\"']/.test(token)) {
      cls = 'str';
    } else if (/^0x[0-9a-fA-F]+$/.test(token) || /^\d/.test(token)) {
      cls = 'nu';
    } else if (/^[A-Za-z_\$@][A-Za-z0-9_\$@]*$/.test(token)) {
      const lower = token.toLowerCase();
      if (keywords.has(lower)) {
        cls = 'kw';
      } else if (/^\$/.test(token) || token === 'this' || token === 'self' || /^@/.test(token)) {
        // Variables and decorators
        cls = 'sp';
      } else {
        const rest = code.slice(regex.lastIndex);
        // If followed by accessors ->, :: or . then likely property access; keep current as 'sp'
        const accessMatch = rest.match(/^\s*(?:->|::|\.)\s*/);
        if (accessMatch) {
          cls = 'sp';
        } else {
          // If next non-space char is '(' => function/method call
          const next = rest.match(/^\s*([\(\[\{])/);
          if (next && next[1] === '(') cls = 'fn';
          else {
            // Heuristic: PascalCase identifiers often types/classes — mark as fn for better visibility
            if (/^[A-Z][A-Za-z0-9_]*$/.test(token)) cls = 'fn';
          }
        }
      }
    }

    const escaped = escapeHtml(token);
    if (cls) out += `<span class="${cls}">${escaped}</span>`;
    else out += escaped;
  }

  return out;
}
